import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useSeo } from '@/lib/useSeo'
import { AuthLayout } from '@/components/public/AuthLayout'

type Decision = 'pending' | 'dashboard' | 'reset' | 'signin-with-error'

/**
 * Target of the magic links Supabase sends (email confirm, password recovery,
 * OAuth return). Supabase-JS exchanges the URL fragment for a session as soon
 * as this component mounts. We then route the user based on the specific auth
 * event that fires.
 *
 * Critical ordering: a password-recovery link also produces a valid session,
 * so we MUST wait for the PASSWORD_RECOVERY event before deciding — otherwise
 * a getSession() shortcut would drop the user on /dashboard instead of the
 * reset form. We do this by:
 *   1. Subscribing to onAuthStateChange first.
 *   2. Short-circuiting immediately on PASSWORD_RECOVERY.
 *   3. Only falling back to getSession() after a small grace period, so any
 *      PASSWORD_RECOVERY event has a chance to arrive first.
 */
export function AuthCallback() {
  useSeo({ title: 'Signing you in', noindex: true })
  const navigate = useNavigate()
  const [decision, setDecision] = useState<Decision>('pending')
  const decidedRef = useRef(false)

  useEffect(() => {
    let active = true

    const decide = (next: Decision) => {
      if (decidedRef.current || !active) return
      decidedRef.current = true
      setDecision(next)
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return
      if (event === 'PASSWORD_RECOVERY') {
        decide('reset')
        return
      }
      if (event === 'SIGNED_IN' && session) {
        decide('dashboard')
      }
    })

    // Fallback: if no event fires within 500ms, consult the session directly.
    // Password-recovery events always fire on mount, so this window only
    // catches the email-confirm / OAuth path when events don't replay.
    const fallback = window.setTimeout(async () => {
      if (decidedRef.current || !active) return
      const { data } = await supabase.auth.getSession()
      if (!active || decidedRef.current) return
      decide(data.session ? 'dashboard' : 'signin-with-error')
    }, 500)

    // Hard timeout: if we never hear anything in 15s, bail.
    const hardTimeout = window.setTimeout(() => {
      decide('signin-with-error')
    }, 15000)

    return () => {
      active = false
      sub.subscription.unsubscribe()
      window.clearTimeout(fallback)
      window.clearTimeout(hardTimeout)
    }
  }, [])

  useEffect(() => {
    if (decision === 'pending') return
    if (decision === 'reset') navigate('/reset-password', { replace: true })
    else if (decision === 'dashboard') navigate('/dashboard', { replace: true })
    else navigate('/signin', { replace: true, state: { error: 'Authentication link expired or invalid.' } })
  }, [decision, navigate])

  return (
    <AuthLayout title="Signing you in…">
      <p className="text-center text-neutral-600" aria-live="polite">
        One moment while we confirm your account.
      </p>
    </AuthLayout>
  )
}
